// HACK TO CLEAN UP insert null bug for entity quests.
const cleanDeltaJson = deltaJson => {
    if (deltaJson && deltaJson.ops) {
        const newDeltaJson = { ...deltaJson, ops: [...deltaJson.ops] }

        for (var op of newDeltaJson.ops) {
            if (typeof op?.insert !== undefined && op?.insert === null) {
                op = { insert: '' }
            }
        }
        return newDeltaJson
    }
    return deltaJson
}

const removeTrailingNewlines = deltaJson => {
    if (!deltaJson) return null

    let newDeltaJson = { ...deltaJson, ops: [...deltaJson.ops] }
    let ops = newDeltaJson?.ops
    if (ops) {
        // Cleanup trailing blank inserts.
        let lastInsertString = ops[ops.length - 1]?.insert
        while (typeof lastInsertString === 'string' && lastInsertString?.trim() === '') {
            ops.pop()
            lastInsertString = ops[ops.length - 1]?.insert
        }

        // Cleanup and trailing newlines in non-blank insert string.
        if (typeof lastInsertString === 'string') {
            ops.pop()
            ops.push({ insert: lastInsertString.trimEnd() })
        }
    }
    return newDeltaJson
}

const getValue = answer => {
    let value = null
    if (answer?.delta_json) {
        value = cleanDeltaJson({ ...answer.delta_json })
    } else {
        value = answer?.content
    }
    return value
}

/**
 * Extracts text from a Delta JSON object, optionally wrapping usernames.
 * @param {Object} deltaJson - The Delta JSON object containing the text.
 * @param {Object} [options] - Optional configuration.
 * @param {boolean} [options.wrapUsernames=false] - Whether to wrap usernames in square brackets.
 * @returns {string|null} The extracted text, or null if deltaJson is falsy.
 */
const getText = (deltaJson, options = {}) => {
    if (deltaJson) {
        const { wrapUsernames = false } = options
        const ops = deltaJson['ops'] || []
        return ops
            .filter(
                op =>
                    typeof op.insert === 'string' ||
                    typeof op.insert === 'number' ||
                    op?.insert?.mention,
            )
            .map(op => {
                if (op?.insert?.mention) {
                    if (op.insert.mention.type === 'model') {
                        return `@@${op.insert.mention.content}`
                    } else {
                        // BACKWARD COMPATIBILITY : old mentions had denotationChar as empty string and content with leading '@'
                        let username = op.insert.mention.content
                        username = username || op.insert.mention.value || ''
                        // Remove @ if it exists at the start
                        if (username.startsWith('@')) {
                            username = username.substring(1)
                        }

                        return username ? (wrapUsernames ? `@[${username}]` : `@${username}`) : ''
                    }
                }
                // Explicitly convert to string to handle numbers
                return String(op?.insert || '')
            })
            .join('')
            .trim()
    } else {
        return null
    }
}

const getImages = deltaJson => {
    if (deltaJson) {
        const ops = deltaJson['ops'] || []
        const images = ops.filter(op => op?.insert?.image).map(op => op.image)
        return images?.length > 0 ? images : null
    } else {
        return null
    }
}

const isEmpty = deltaJson => {
    try {
        if (deltaJson) {
            const ops = deltaJson['ops'] || []

            // If no ops, it's empty
            if (ops.length === 0) return true

            // First check for model mentions - if we find one, the content is not empty
            const hasModelMention = ops.some(op => op?.insert?.mention?.denotationChar === '@@')
            if (hasModelMention) return false

            // If only one op, check if it's truly empty
            if (ops.length === 1) {
                const insert = ops[0].insert

                // Handle string inserts
                if (typeof insert === 'string') {
                    return insert.trim() === ''
                }

                // Handle object inserts (like mentions)
                if (typeof insert === 'object' && insert !== null) {
                    return false
                }
            }

            // More than one op means it's not empty
            return false
        } else return true
    } catch (e) {
        console.error('Error in isEmpty check:', e)
        return true
    }
}

/**
 * Checks if a delta contains a model mention
 * @param {Object} delta - The Quill delta object to check
 * @returns {boolean} - True if the delta contains a model mention
 */
const containsModelMention = delta => {
    if (!delta || !delta.ops) return false

    // Debug: log the delta content to see what we're working with
    // console.log('Checking delta for model mentions:', JSON.stringify(delta.ops));

    // Check each operation for a model mention
    return delta.ops.some(op => {
        if (op.insert && typeof op.insert === 'object' && op.insert.mention) {
            const mention = op.insert.mention
            const isModel = mention.type === 'model'
            //console.log('Found mention:', mention, 'isModel:', isModel);
            return isModel
        }
        return false
    })
}

const createModelMention = model => {
    return {
        id: model,
        value: model,
        content: model,
        denotationChar: '@@',
        type: 'model',
        link: `/models/${model}`,
        isModel: true,
    }
}

/* =========================================================
 *  Quill Delta  <->  ProseMirror JSON  Converter (minimal)
 * ---------------------------------------------------------
 *  This helps us migrate to Tiptap/ProseMirror while keeping
 *  legacy data accessible.  The converter focuses on the
 *  formats we actually use in production:
 *    • paragraphs / hard-returns
 *    • basic marks: bold, italic, underline, strike, code
 *    • link mark
 *    • custom inline nodes: mention (user / model)
 *    • image / video embeds
 *  Anything unknown is transferred as plain text.
 *  The output JSON follows the default Tiptap/StarterKit
 *  schema (`type:"doc" -> content:[{type:"paragraph"...}]`).
 * =======================================================*/

// Maps Quill attribute keys → ProseMirror mark types
const ATTR_TO_MARK = {
    bold: 'bold',
    italic: 'italic',
    underline: 'underline',
    strike: 'strike',
    code: 'code',
}

// Helper: convert Quill attributes object → ProseMirror marks array
function attrsToMarks(attrs = {}) {
    const marks = []
    for (const key in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, key)) continue
        if (key === 'link' && attrs.link) {
            marks.push({ type: 'link', attrs: { href: attrs.link } })
            continue
        }
        const markType = ATTR_TO_MARK[key]
        if (markType && attrs[key]) marks.push({ type: markType })
    }
    return marks.length ? marks : undefined
}

function deltaToPmJson(deltaJson) {
    if (!deltaJson || !Array.isArray(deltaJson.ops)) {
        return { type: 'doc', content: [] }
    }

    // First pass: Group ops by their block type
    // In Quill Delta, block attributes are on the newline AFTER the content
    const blocks = []
    let currentBlock = { type: 'paragraph', ops: [] }

    for (let i = 0; i < deltaJson.ops.length; i++) {
        const op = deltaJson.ops[i]
        const nextOp = deltaJson.ops[i + 1]

        if (typeof op.insert === 'string') {
            // Check if this text is followed by a newline with block attributes
            const hasBlockNewline =
                nextOp &&
                nextOp.insert === '\n' &&
                (nextOp.attributes?.list || nextOp.attributes?.['code-block'])

            if (op.insert === '\n') {
                // This is a newline, check for block attributes
                if (op.attributes?.list) {
                    currentBlock.type = 'list'
                    currentBlock.listType = op.attributes.list
                } else if (op.attributes?.['code-block']) {
                    currentBlock.type = 'code-block'
                }
                blocks.push(currentBlock)
                currentBlock = { type: 'paragraph', ops: [] }
            } else {
                // Regular text - might contain internal newlines
                const lines = op.insert.split('\n')
                lines.forEach((line, idx) => {
                    if (line) {
                        currentBlock.ops.push({ ...op, insert: line })
                    }
                    if (idx < lines.length - 1) {
                        // Internal newline - but check if we should mark this block
                        if (hasBlockNewline) {
                            // The next op is a newline with block attrs, so this line is part of that block
                            if (nextOp.attributes?.list) {
                                currentBlock.type = 'list'
                                currentBlock.listType = nextOp.attributes.list
                            } else if (nextOp.attributes?.['code-block']) {
                                currentBlock.type = 'code-block'
                            }
                        }
                        blocks.push(currentBlock)
                        currentBlock = { type: 'paragraph', ops: [] }
                    }
                })
            }
        } else {
            // Object inserts (images, videos, mentions, etc.)
            currentBlock.ops.push(op)
        }
    }

    // Don't forget the last block
    if (currentBlock.ops.length > 0 || blocks.length === 0) {
        blocks.push(currentBlock)
    }

    // Second pass: Convert blocks to ProseMirror nodes
    const doc = { type: 'doc', content: [] }
    let currentList = null
    let codeBlockLines = []
    let inCodeBlock = false

    blocks.forEach((block, idx) => {
        if (block.type === 'list') {
            // Handle list items
            const listType = block.listType === 'bullet' ? 'bulletList' : 'orderedList'

            if (!currentList || currentList.type !== listType) {
                if (currentList) {
                    doc.content.push(currentList)
                }
                currentList = { type: listType, content: [] }
            }

            const listItem = { type: 'listItem', content: [] }
            const paragraph = { type: 'paragraph', content: [] }

            block.ops.forEach(op => {
                if (typeof op.insert === 'string') {
                    const node = { type: 'text', text: op.insert }
                    const marks = attrsToMarks(op.attributes)
                    if (marks) node.marks = marks
                    paragraph.content.push(node)
                } else if (op.insert?.mention) {
                    const mention = op.insert.mention
                    paragraph.content.push({
                        type: 'mention',
                        attrs: {
                            id: mention.id,
                            label: mention.content || mention.value || mention.id,
                            type: mention.type || 'user',
                            denotationChar: mention.denotationChar || '@',
                            content: mention.content || mention.value || mention.id,
                            link:
                                mention.link ||
                                (mention.type === 'model'
                                    ? `/models/${mention.id}`
                                    : `/users/${mention.id}`),
                        },
                    })
                }
            })

            if (paragraph.content.length > 0) {
                listItem.content.push(paragraph)
            }
            currentList.content.push(listItem)
        } else if (block.type === 'code-block') {
            // End any current list
            if (currentList) {
                doc.content.push(currentList)
                currentList = null
            }

            // Accumulate code block lines
            if (!inCodeBlock) {
                inCodeBlock = true
                codeBlockLines = []
            }

            const text = block.ops
                .map(op => (typeof op.insert === 'string' ? op.insert : ''))
                .join('')
            codeBlockLines.push(text)

            // Check if the next block is also a code block
            const nextBlock = blocks[idx + 1]
            if (!nextBlock || nextBlock.type !== 'code-block') {
                // End of code block
                doc.content.push({
                    type: 'codeBlock',
                    content:
                        codeBlockLines.length > 0
                            ? [
                                  {
                                      type: 'text',
                                      text: codeBlockLines.join('\n'),
                                  },
                              ]
                            : [],
                })
                inCodeBlock = false
                codeBlockLines = []
            }
        } else {
            // Regular paragraph
            // End any current list
            if (currentList) {
                doc.content.push(currentList)
                currentList = null
            }

            const paragraph = { type: 'paragraph', content: [] }

            block.ops.forEach(op => {
                if (typeof op.insert === 'string') {
                    const node = { type: 'text', text: op.insert }
                    const marks = attrsToMarks(op.attributes)
                    if (marks) node.marks = marks
                    paragraph.content.push(node)
                } else if (op.insert?.mention) {
                    const mention = op.insert.mention
                    paragraph.content.push({
                        type: 'mention',
                        attrs: {
                            id: mention.id,
                            label: mention.content || mention.value || mention.id,
                            type: mention.type || 'user',
                            denotationChar: mention.denotationChar || '@',
                            content: mention.content || mention.value || mention.id,
                            link:
                                mention.link ||
                                (mention.type === 'model'
                                    ? `/models/${mention.id}`
                                    : `/users/${mention.id}`),
                        },
                    })
                } else if (op.insert?.['more-button']) {
                    const moreButton = op.insert['more-button']
                    paragraph.content.push({
                        type: 'moreButton',
                        attrs: {
                            buttonId: moreButton.id || 'expand-button',
                            instanceId: moreButton.instanceId || 'unknown',
                        },
                    })
                } else if (op.insert?.image) {
                    // Images go directly into doc, not paragraphs
                    if (paragraph.content.length > 0) {
                        doc.content.push(paragraph)
                        paragraph.content = []
                    }
                    doc.content.push({ type: 'image', attrs: { src: op.insert.image } })
                } else if (op.insert?.video) {
                    // Videos go directly into doc, not paragraphs
                    if (paragraph.content.length > 0) {
                        doc.content.push(paragraph)
                        paragraph.content = []
                    }
                    doc.content.push({ type: 'video', attrs: { src: op.insert.video } })
                }
            })

            // Push paragraph if it has content
            if (paragraph.content.length > 0) {
                doc.content.push(paragraph)
            }
        }
    })

    // Don't forget any trailing list
    if (currentList) {
        doc.content.push(currentList)
    }

    // Ensure doc has at least one paragraph
    if (doc.content.length === 0) {
        doc.content.push({ type: 'paragraph', content: [] })
    }

    return doc
}

// Reverse converter (PM JSON → Quill Delta)
function pmJsonToDelta(pmJson) {
    const delta = { ops: [] }
    if (!pmJson || !Array.isArray(pmJson?.content)) return delta

    const pushTextOp = (text, marks) => {
        const op = { insert: text }
        if (marks && marks.length) {
            const attrs = {}
            marks.forEach(mark => {
                if (!mark) return
                if (mark.type === 'link') attrs.link = mark.attrs?.href
                else attrs[mark.type] = true
            })
            if (Object.keys(attrs).length) op.attributes = attrs
        }
        delta.ops.push(op)
    }

    pmJson.content.forEach((node, nodeIdx) => {
        if (node.type === 'paragraph') {
            if (!node.content || node.content.length === 0) {
                // empty paragraph → newline
                delta.ops.push({ insert: '\n' })
            } else {
                node.content.forEach(child => {
                    if (child.type === 'text') {
                        pushTextOp(child.text, child.marks)
                    } else if (child.type === 'mention') {
                        // Convert mention back to Delta format
                        const mentionData = {
                            id: child.attrs.id,
                            value: child.attrs.label || child.attrs.content || child.attrs.id,
                            content: child.attrs.content || child.attrs.label || child.attrs.id,
                            type: child.attrs.type || 'user',
                            denotationChar: child.attrs.denotationChar || '@',
                            link:
                                child.attrs.link ||
                                (child.attrs.type === 'model'
                                    ? `/models/${child.attrs.id}`
                                    : `/users/${child.attrs.id}`),
                        }
                        delta.ops.push({ insert: { mention: mentionData } })
                    } else if (child.type === 'moreButton') {
                        // Convert moreButton back to Delta format
                        delta.ops.push({
                            insert: {
                                'more-button': {
                                    id: child.attrs.buttonId || 'expand-button',
                                    instanceId: child.attrs.instanceId || 'unknown',
                                },
                            },
                        })
                    } else if (child.type === 'hardBreak') {
                        // Hard line break (Shift+Enter) inside a paragraph
                        // Represent as a newline character inside the same paragraph.
                        // This mirrors Quill\'s behaviour where a plain "\n" inside
                        // text denotes a soft line-break.
                        delta.ops.push({ insert: '\n' })
                    }
                })
                // newline at paragraph end
                delta.ops.push({ insert: '\n' })
            }
        } else if (node.type === 'bulletList' || node.type === 'orderedList') {
            // Handle list nodes
            const listType = node.type === 'bulletList' ? 'bullet' : 'ordered'
            if (node.content) {
                node.content.forEach(listItem => {
                    if (listItem.type === 'listItem' && listItem.content) {
                        // Process each paragraph/content inside the list item
                        listItem.content.forEach(itemContent => {
                            if (itemContent.type === 'paragraph' && itemContent.content) {
                                itemContent.content.forEach(child => {
                                    if (child.type === 'text') {
                                        pushTextOp(child.text, child.marks)
                                    } else if (child.type === 'mention') {
                                        // Handle mentions in lists
                                        const mentionData = {
                                            id: child.attrs.id,
                                            value:
                                                child.attrs.label ||
                                                child.attrs.content ||
                                                child.attrs.id,
                                            content:
                                                child.attrs.content ||
                                                child.attrs.label ||
                                                child.attrs.id,
                                            type: child.attrs.type || 'user',
                                            denotationChar: child.attrs.denotationChar || '@',
                                            link:
                                                child.attrs.link ||
                                                (child.attrs.type === 'model'
                                                    ? `/models/${child.attrs.id}`
                                                    : `/users/${child.attrs.id}`),
                                        }
                                        delta.ops.push({ insert: { mention: mentionData } })
                                    }
                                })
                            }
                        })
                        // Add newline with list attribute
                        delta.ops.push({ insert: '\n', attributes: { list: listType } })
                    }
                })
            }
        } else if (node.type === 'codeBlock') {
            // Handle code blocks
            if (node.content) {
                // Collect all text from the code block
                let codeText = ''
                node.content.forEach(child => {
                    if (child.type === 'text') {
                        codeText += child.text
                    }
                })
                // Split by newlines and add each line with code-block attribute
                const lines = codeText.split('\n')
                lines.forEach((line, idx) => {
                    if (line || idx < lines.length - 1) {
                        delta.ops.push({ insert: line })
                        delta.ops.push({ insert: '\n', attributes: { 'code-block': true } })
                    }
                })
            } else {
                // Empty code block
                delta.ops.push({ insert: '\n', attributes: { 'code-block': true } })
            }
        } else if (node.type === 'image') {
            delta.ops.push({ insert: { image: node.attrs?.src } })
            delta.ops.push({ insert: '\n' })
        } else if (node.type === 'video') {
            delta.ops.push({ insert: { video: node.attrs?.src } })
            delta.ops.push({ insert: '\n' })
        }
    })

    // Remove trailing newline for compatibility with legacy content
    // BUT keep newlines that have block attributes (list, code-block, etc.)
    while (delta.ops.length) {
        const lastOp = delta.ops[delta.ops.length - 1]
        if (lastOp.insert === '\n' && !lastOp.attributes) {
            delta.ops.pop()
        } else {
            break
        }
    }

    // Guarantee at least one op
    if (!delta.ops.length) delta.ops.push({ insert: '' })

    return delta
}

export {
    getText,
    getImages,
    getValue,
    isEmpty,
    removeTrailingNewlines,
    containsModelMention,
    createModelMention,
    deltaToPmJson,
    pmJsonToDelta,
}
